import { useState, useRef, useEffect, FormEvent, useCallback, type ComponentPropsWithoutRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '@/lib/auth';
import AppLayout from '@/components/AppLayout';
import CommandBar from '@/components/chat/CommandBar';
import MetricStrip from '@/components/chat/MetricStrip';
import InsightPanel, { parseInsightBlocks } from '@/components/chat/InsightPanel';
import { DEFAULT_DATE_RANGE, type DateRange } from '@/components/chat/DateRangePicker';
import '@/styles/chat.css';

const markdownComponents = {
  table: ({ children, ...props }: ComponentPropsWithoutRef<'table'>) => (
    <div className="prose-table-wrap">
      <table {...props}>{children}</table>
    </div>
  ),
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ConversationSummary {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export default function ChatPage() {
  const { user, refresh } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [activeConversationTitle, setActiveConversationTitle] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [goal, setGoal] = useState('');
  const [campaignType, setCampaignType] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [selectedAccountId, setSelectedAccountId] = useState(user?.default_customer_id || '');
  const [selectedAccountName, setSelectedAccountName] = useState(user?.default_customer_name || '');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const isInternalUser = user?.email?.toLowerCase().endsWith('@zimamedia.com');
  const hasActiveSubscription = isInternalUser ||
    (user?.plan && user.plan !== 'starter' && user.plan !== 'waitlist' &&
     (!user.subscription_status || ['active', 'trialing'].includes(user.subscription_status)));
  const subscriptionBlocked = !hasActiveSubscription;

  const scrollToBottom = () => {
    chatContainerRef.current?.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch('/api/conversations');
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleResumeConversation = async (convoId: number) => {
    try {
      const res = await fetch(`/api/conversations/${convoId}/resume`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        const convo = data.conversation;
        setActiveConversationId(convo.id);
        setActiveConversationTitle(convo.title || null);
        setMessages(
          (convo.messages || [])
            .filter((m: { role: string }) => m.role === 'user' || m.role === 'model')
            .map((m: { role: string; content: string }) => ({
              role: m.role === 'user' ? 'user' as const : 'assistant' as const,
              content: m.content,
            }))
        );
        setSidebarOpen(false);
      }
    } catch {}
  };

  const handleDeleteConversation = async (convoId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(`/api/conversations/${convoId}`, { method: 'DELETE' });
      if (res.ok) {
        setConversations((prev) => prev.filter((c) => c.id !== convoId));
        if (activeConversationId === convoId) {
          setActiveConversationId(null);
          setMessages([]);
        }
      }
    } catch {}
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const message = input.trim();
    if (!message || isLoading) return;

    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setIsLoading(true);

    try {
      const body: Record<string, unknown> = { message };
      if (activeConversationId) {
        body.conversation_id = activeConversationId;
      }
      body.context = {
        account_id: selectedAccountId,
        goal,
        campaign_type: campaignType,
        date_start: dateRange.startDate,
        date_end: dateRange.endDate,
        date_preset: dateRange.preset,
        date_label: dateRange.label,
      };
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `Error: ${data.error}` },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.response },
        ]);
        if (data.conversation_id) {
          setActiveConversationId(data.conversation_id);
        }
        if (data.conversation_title) {
          setActiveConversationTitle(data.conversation_title);
        }
        loadConversations();
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Failed to connect to the server. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
      textareaRef.current?.focus();
      refresh();
    }
  };

  const handleReset = async () => {
    try {
      await fetch('/api/reset', { method: 'POST' });
    } catch {}
    setMessages([]);
    setActiveConversationId(null);
    setActiveConversationTitle(null);
    setGoal('');
    setCampaignType('');
    loadConversations();
  };

  const handleSuggestion = (msg: string) => {
    setInput(msg);
    setTimeout(() => {
      const form = document.getElementById('chat-form') as HTMLFormElement;
      form?.requestSubmit();
    }, 0);
  };

  const handleCommandPrompt = (prompt: string) => {
    handleSuggestion(prompt);
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading) {
        const form = document.getElementById('chat-form') as HTMLFormElement;
        form?.requestSubmit();
      }
    }
  };

  const suggestionMap: Record<string, Record<string, { label: string; msg: string }[]>> = {
    '': {
      '': [
        { label: 'Account overview', msg: 'Give me a quick performance overview of this account for the last 30 days' },
        { label: 'Show all campaigns', msg: 'Show me all campaigns and their status' },
        { label: 'Top ad groups', msg: 'What are my top performing ad groups this month?' },
        { label: 'Keyword performance', msg: 'Show me keyword performance for the last 7 days' },
      ],
      SEARCH: [
        { label: 'Search campaign overview', msg: 'Show me all Search campaigns and their performance' },
        { label: 'Top keywords', msg: 'What are the top performing keywords across Search campaigns?' },
        { label: 'Ad copy performance', msg: 'Which Search ads have the best CTR?' },
        { label: 'Quality scores', msg: 'Show keywords with low quality scores that need attention' },
      ],
      SHOPPING: [
        { label: 'Shopping overview', msg: 'Show me all Shopping campaigns and their performance' },
        { label: 'Top products', msg: 'Which products are driving the most conversions?' },
        { label: 'Product ROAS', msg: 'Show ROAS by product group for Shopping campaigns' },
        { label: 'Low performers', msg: 'Find Shopping products with high spend and no conversions' },
      ],
      PERFORMANCE_MAX: [
        { label: 'PMax overview', msg: 'Show me all Performance Max campaigns and their results' },
        { label: 'Asset group performance', msg: 'How are my PMax asset groups performing?' },
        { label: 'PMax conversions', msg: 'Which Performance Max campaigns have the most conversions?' },
        { label: 'PMax spend efficiency', msg: 'Show CPA trends for my Performance Max campaigns' },
      ],
      VIDEO: [
        { label: 'Video overview', msg: 'Show me all Video campaigns and their performance' },
        { label: 'View rates', msg: 'Which Video campaigns have the best view rates?' },
        { label: 'Video conversions', msg: 'Show conversions from Video campaigns this month' },
        { label: 'Video CPV', msg: 'What is the cost-per-view across my Video campaigns?' },
      ],
      DISPLAY: [
        { label: 'Display overview', msg: 'Show me all Display campaigns and their performance' },
        { label: 'Display CTR', msg: 'Which Display campaigns have the best click-through rates?' },
        { label: 'Display conversions', msg: 'Show conversions from Display campaigns this month' },
        { label: 'Display placements', msg: 'What are my top performing Display placements?' },
      ],
    },
    efficiency: {
      '': [
        { label: 'High CPA keywords', msg: 'Find high-CPA keywords I should consider pausing' },
        { label: 'Above-average CPA', msg: 'Show campaigns with above-average CPA' },
        { label: 'Wasted spend', msg: 'Where am I wasting the most budget with low conversions?' },
        { label: 'Cost-saving opportunities', msg: 'Identify campaigns where I can reduce spend without losing conversions' },
      ],
      SEARCH: [
        { label: 'Expensive keywords', msg: 'Which Search keywords have the highest CPA?' },
        { label: 'Low quality scores', msg: 'Find Search keywords with low quality scores driving up costs' },
        { label: 'Negative keyword gaps', msg: 'Suggest negative keywords to reduce wasted Search spend' },
        { label: 'Search efficiency', msg: 'Show Search campaigns ranked by cost efficiency' },
      ],
      SHOPPING: [
        { label: 'Unprofitable products', msg: 'Find Shopping products with high spend and no conversions' },
        { label: 'Shopping CPA', msg: 'Which Shopping campaigns have above-average CPA?' },
        { label: 'Low ROAS products', msg: 'Show Shopping products with ROAS below target' },
        { label: 'Shopping budget waste', msg: 'Where is my Shopping budget being wasted?' },
      ],
      PERFORMANCE_MAX: [
        { label: 'PMax CPA check', msg: 'Which Performance Max campaigns have the highest CPA?' },
        { label: 'PMax efficiency', msg: 'Show Performance Max campaigns ranked by cost efficiency' },
        { label: 'PMax budget review', msg: 'Are any PMax campaigns overspending relative to conversions?' },
        { label: 'PMax optimization', msg: 'Find PMax campaigns with room to improve CPA' },
      ],
      VIDEO: [
        { label: 'Video CPV analysis', msg: 'Which Video campaigns have the highest cost-per-view?' },
        { label: 'Video spend waste', msg: 'Find Video campaigns with high spend and low engagement' },
        { label: 'Video CPA', msg: 'Show Video campaigns ranked by CPA' },
        { label: 'Video efficiency', msg: 'Which Video ads are most cost-efficient?' },
      ],
      DISPLAY: [
        { label: 'Display CPA check', msg: 'Which Display campaigns have above-average CPA?' },
        { label: 'Display waste', msg: 'Find Display placements with high impressions but no clicks' },
        { label: 'Display efficiency', msg: 'Show Display campaigns ranked by cost-per-conversion' },
        { label: 'Display optimization', msg: 'Where can I cut Display spend without losing conversions?' },
      ],
    },
    scale: {
      '': [
        { label: 'Room to scale', msg: 'Which campaigns have room to scale?' },
        { label: 'Underspending winners', msg: 'Find underspending high-converting campaigns' },
        { label: 'Budget-limited campaigns', msg: 'Show campaigns that are limited by budget' },
        { label: 'Scaling opportunities', msg: 'What are my best opportunities to increase spend profitably?' },
      ],
      SEARCH: [
        { label: 'Search scale potential', msg: 'Which Search campaigns have impression share to capture?' },
        { label: 'Budget-limited Search', msg: 'Show Search campaigns limited by budget' },
        { label: 'Top Search keywords', msg: 'Find top Search keywords where I could increase bids' },
        { label: 'Search expansion', msg: 'Which Search ad groups should I expand?' },
      ],
      SHOPPING: [
        { label: 'Shopping scale potential', msg: 'Which Shopping campaigns could handle more budget?' },
        { label: 'Top Shopping products', msg: 'Find top converting Shopping products to push more' },
        { label: 'Shopping impression share', msg: 'Show Shopping campaigns with low impression share' },
        { label: 'Shopping expansion', msg: 'Which product groups should I expand in Shopping?' },
      ],
      PERFORMANCE_MAX: [
        { label: 'PMax scale potential', msg: 'Which PMax campaigns could benefit from more budget?' },
        { label: 'PMax growth areas', msg: 'Show PMax campaigns with strong conversion rates to scale' },
        { label: 'PMax budget headroom', msg: 'Which PMax campaigns are budget-constrained?' },
        { label: 'PMax expansion', msg: 'Where should I expand my Performance Max campaigns?' },
      ],
      VIDEO: [
        { label: 'Video scale potential', msg: 'Which Video campaigns could reach more audience?' },
        { label: 'Video growth', msg: 'Find Video campaigns with strong engagement to scale' },
        { label: 'Video budget headroom', msg: 'Show Video campaigns limited by budget' },
        { label: 'Video expansion', msg: 'Which Video audiences should I target for growth?' },
      ],
      DISPLAY: [
        { label: 'Display scale potential', msg: 'Which Display campaigns have room to grow?' },
        { label: 'Display growth', msg: 'Find Display campaigns with strong conversions to scale' },
        { label: 'Display audience expansion', msg: 'Which Display audiences should I expand?' },
        { label: 'Display reach', msg: 'Show Display campaigns with low impression share' },
      ],
    },
    max_revenue: {
      '': [
        { label: 'ROAS by campaign', msg: 'Show ROAS by campaign' },
        { label: 'Best return products', msg: 'Which products have the best return?' },
        { label: 'Revenue leaders', msg: 'Show my top revenue-generating campaigns' },
        { label: 'Revenue growth', msg: 'Where can I increase revenue the most?' },
      ],
      SEARCH: [
        { label: 'Search ROAS', msg: 'Show Search campaigns ranked by ROAS' },
        { label: 'Search revenue keywords', msg: 'Which Search keywords drive the most revenue?' },
        { label: 'Search conversion value', msg: 'Show conversion value trends for Search campaigns' },
        { label: 'Search revenue growth', msg: 'Where can I grow Search revenue?' },
      ],
      SHOPPING: [
        { label: 'Shopping ROAS', msg: 'Show Shopping campaigns ranked by ROAS' },
        { label: 'Top revenue products', msg: 'Which Shopping products generate the most revenue?' },
        { label: 'Shopping revenue trends', msg: 'Show revenue trends for my Shopping campaigns' },
        { label: 'Shopping revenue growth', msg: 'Which Shopping product groups could drive more revenue?' },
      ],
      PERFORMANCE_MAX: [
        { label: 'PMax ROAS', msg: 'Show Performance Max campaigns ranked by ROAS' },
        { label: 'PMax revenue', msg: 'Which PMax campaigns drive the most conversion value?' },
        { label: 'PMax revenue trends', msg: 'Show revenue trends for Performance Max campaigns' },
        { label: 'PMax revenue growth', msg: 'How can I maximize revenue from PMax campaigns?' },
      ],
      VIDEO: [
        { label: 'Video ROAS', msg: 'Show Video campaigns ranked by conversion value' },
        { label: 'Video revenue', msg: 'Which Video campaigns drive the most revenue?' },
        { label: 'Video revenue trends', msg: 'Show revenue trends from Video campaigns' },
        { label: 'Video revenue potential', msg: 'Which Video campaigns have the best revenue potential?' },
      ],
      DISPLAY: [
        { label: 'Display ROAS', msg: 'Show Display campaigns ranked by ROAS' },
        { label: 'Display revenue', msg: 'Which Display campaigns generate the most conversion value?' },
        { label: 'Display revenue trends', msg: 'Show revenue trends from Display campaigns' },
        { label: 'Display revenue growth', msg: 'Where can I grow Display revenue?' },
      ],
    },
  };

  const suggestions = (() => {
    const goalKey = goal || '';
    const typeKey = campaignType || '';
    const goalSuggestions = suggestionMap[goalKey] || suggestionMap[''];
    return goalSuggestions[typeKey] || goalSuggestions[''] || suggestionMap[''][''];
  })();

  const firstName = user?.name?.split(' ')[0];

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <AppLayout>
      {sidebarOpen && (
        <div className="chat-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`chat-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="chat-sidebar-header">
          <span className="chat-sidebar-title">Conversations</span>
          <button className="chat-sidebar-close" onClick={() => setSidebarOpen(false)}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="chat-sidebar-list">
          {conversations.length === 0 ? (
            <div className="chat-sidebar-empty">No conversations yet</div>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                className={`chat-sidebar-item ${activeConversationId === c.id ? 'active' : ''}`}
                onClick={() => handleResumeConversation(c.id)}
              >
                <div className="chat-sidebar-item-content">
                  <div className="chat-sidebar-item-title">{c.title}</div>
                  <div className="chat-sidebar-item-meta">
                    {c.message_count} messages · {formatDate(c.updated_at)}
                  </div>
                </div>
                <button
                  className="chat-sidebar-item-delete"
                  onClick={(e) => handleDeleteConversation(c.id, e)}
                  title="Delete conversation"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <CommandBar
        onSendPrompt={handleCommandPrompt}
        onGoalChange={setGoal}
        onCampaignTypeChange={setCampaignType}
        onDateRangeChange={setDateRange}
        onAccountChange={(id, name) => {
          setSelectedAccountId(id);
          setSelectedAccountName(name);
          setMessages([]);
          setActiveConversationId(null);
          setActiveConversationTitle(null);
          fetch('/api/reset', { method: 'POST' }).catch(() => {});
          loadConversations();
        }}
        onRestoreContext={(ctx) => {
          setGoal(ctx.goal || '');
          setCampaignType(ctx.campaign_type || '');
          if (ctx.account_id) {
            setSelectedAccountId(ctx.account_id);
            setMessages([]);
            setActiveConversationId(null);
            setActiveConversationTitle(null);
            fetch('/api/reset', { method: 'POST' }).catch(() => {});
          }
        }}
        goal={goal}
        campaignType={campaignType}
        selectedAccountId={selectedAccountId}
        dateRange={dateRange}
        disabled={isLoading}
      />
      <MetricStrip accountId={selectedAccountId} campaignType={campaignType} dateRange={dateRange} />

      {activeConversationTitle && messages.length > 0 && (
        <div className="chat-active-title">
          <span>{activeConversationTitle}</span>
        </div>
      )}

      <div ref={chatContainerRef} className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-welcome">
            {!user?.mcp_enabled ? (
              <>
                <div className="chat-welcome-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
                <h2>Welcome, {firstName}</h2>
                <p>
                  {!user?.google_ads_connected
                    ? 'Link your Google Ads account to get started with AI-powered campaign analysis.'
                    : 'Select a default customer account to start chatting with your Google Ads data.'}
                </p>
                <Link to="/account" className="btn btn-primary" style={{ marginTop: 8 }}>
                  {!user?.google_ads_connected ? 'Link Google Ads' : 'Select Customer Account'}
                </Link>
              </>
            ) : (
              <>
                <div className="chat-welcome-icon">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                    <line x1="9" y1="9" x2="9.01" y2="9"></line>
                    <line x1="15" y1="9" x2="15.01" y2="9"></line>
                  </svg>
                </div>
                <h2>Welcome, {firstName}</h2>
                <p>Ask me anything about your Google Ads data. Try one of these:</p>
                <div className="chat-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => handleSuggestion(s.msg)}
                      className="chat-suggestion-btn"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="chat-messages-list">
            {messages.map((msg, i) => {
              if (msg.role === 'assistant') {
                const { text, insightBlocks } = parseInsightBlocks(msg.content);
                return (
                  <div key={i} className="chat-message">
                    <div className="chat-avatar ai-avatar">AI</div>
                    <div className="chat-bubble ai-bubble">
                      {text && (
                        <div className="prose">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{text}</ReactMarkdown>
                        </div>
                      )}
                      {insightBlocks.map((block, bi) => (
                        <InsightPanel key={bi} data={block} onAction={handleCommandPrompt} />
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div key={i} className="chat-message user">
                  <div className="chat-avatar user-avatar">U</div>
                  <div className="chat-bubble user-bubble">{msg.content}</div>
                </div>
              );
            })}

            {isLoading && (
              <div className="chat-message animate-fadeIn">
                <div className="chat-avatar ai-avatar">AI</div>
                <div className="chat-bubble ai-bubble">
                  <div className="chat-loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="chat-composer">
        {subscriptionBlocked ? (
          <div className="chat-upgrade-prompt">
            <div className="chat-upgrade-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </div>
            <p className="chat-upgrade-title">Subscription Required</p>
            <p className="chat-upgrade-text">Subscribe to Starter or Agency to access AI-powered Google Ads management.</p>
            <Link to="/pricing" className="btn btn-primary">View Plans</Link>
          </div>
        ) : (
          <>
            <div className="chat-actions">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="chat-history-btn"
                title="Conversation history"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                History
                {conversations.length > 0 && (
                  <span className="chat-history-count">{conversations.length}</span>
                )}
              </button>
              <button onClick={handleReset} className="chat-reset-btn">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Chat
              </button>
            </div>
            <form id="chat-form" onSubmit={handleSubmit}>
              <div className="chat-input-wrap">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleTextareaInput}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your Google Ads data..."
                  rows={1}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="chat-send-btn"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </AppLayout>
  );
}
